import type { ImageCrop } from './types.js';

/**
 * Turning a detected face into photo framing.
 *
 * Shared by the widget (a shopper's fresh upload lands framed on the face) and
 * the studio (a test picture on a cut-out slot frames the same way), so the
 * author previews exactly the framing the shopper's photo will get.
 */

/** A detected face, in fractions of the image so it survives any resize. */
export interface FaceBox {
  x: number;
  y: number;
  w: number;
  h: number;
  /** 0–1 from the detector; used only to order the choices. */
  score?: number;
}

/**
 * How much of the photo to keep around the chosen face.
 *
 * A face box is the face alone — crop to it exactly and you get a portrait
 * cropped at the hairline and chin, which looks like a mistake. Real head-and
 * -shoulders framing wants roughly three times the box.
 */
const HEAD_ROOM = 3.0;

/**
 * Turns a face box into the crop the scene understands.
 *
 * `ImageCrop` is centre-plus-zoom in fractions of the source, which is exactly
 * what the on-canvas photo editor already writes — so a picked face and a
 * dragged photo produce the same shape of value and the renderer needs no new
 * code path.
 */
export function cropForFace(face: FaceBox): ImageCrop {
  const cx = face.x + face.w / 2;
  const cy = face.y + face.h / 2;
  // The wider of the two dimensions decides the zoom, so a tall narrow box does
  // not zoom in past the ears.
  const extent = Math.max(face.w, face.h) * HEAD_ROOM;
  return {
    // Kept inside the image: a face at the very edge would otherwise ask the
    // renderer to sample outside the photo.
    cx: Math.min(1, Math.max(0, cx)),
    cy: Math.min(1, Math.max(0, cy)),
    // zoom 1 is the plain cover fit; a face filling a third of the frame is 3x.
    zoom: Math.min(6, Math.max(1, 1 / Math.min(1, extent))),
  };
}
